import React, { useCallback, useState } from 'react'
import { UseStepFunc, defaultStepFormProps } from '../types.d'
import { Route } from 'react-router-dom'
import * as routing from '../../constants/routes'
import { OnboardingStepKey } from '@cbgms/api'
import { useCompleteOnSubmit } from '../use-complete-on-submit'
import { useTranslation } from 'react-i18next'
import { FormValues } from './types.d'
import { useDispatch } from 'react-redux'
import OnboardingAcceptTermsAndConditions from './AcceptTermsAndConditionsForm'
import { acceptTermsAndConditions } from 'app/dashboard/state/actions'
import { getCurrentShop, updateShop } from 'app/shops/state/actions'
import { ShopCountryContextProvider } from '@cbgms/base/shop-country/shopCountryContext'
import { Subscriptions } from '@carsys/enums/subscription'
import { useShouldExecuteStep } from '../use-should-execute-step'
import { AcceptTermsAndConditionsCTA } from './AcceptTermsAndConditionsCTA'
import { useShouldRunInBackground } from '../use-should-run-in-background'
import { useStepData } from '../use-step-data'

interface StepInputProps {
  basePath: string
  country: string
}

interface StepProps {
  basePath: string
  onSubmit: (v: FormValues) => void
  country: string
  tempShopID: string // is used to provide data to some hooks. we don't have a shop id yet at the point where we show this to users
  modules: Subscriptions[]
}

export const useAcceptTermsAndConditionsStep: UseStepFunc<StepProps, StepInputProps> = ({ basePath, country, onboardingInfo }) => {
  const dispatch = useDispatch()
  const [isAcceptedEULA, setIsAcceptedEULA] = useState<boolean>(false)
  const [isAcceptedHaynesPro, setIsAcceptedHaynesPro] = useState<boolean>(false)
  const stepName = OnboardingStepKey.TermsAndConditions
  const stepData = useStepData(stepName, onboardingInfo)
  const shouldExecuteStep = useShouldExecuteStep(stepName, onboardingInfo)
  const shouldRunInBackground = useShouldRunInBackground(stepName, onboardingInfo)

  const onStepSubmit = (values: FormValues) => {
    setIsAcceptedEULA(values.AcceptedEULA)
    setIsAcceptedHaynesPro(values.AcceptedHaynesPro)
  }
  const complete = useCompleteOnSubmit(onStepSubmit)
  const onPersist = useCallback(async () => {
    if (isAcceptedEULA) {
      await dispatch(acceptTermsAndConditions())
    }
    if (isAcceptedHaynesPro) {
      // get latest values for the shop, and persist this
      const shopData = await dispatch(getCurrentShop())
      const shop = shopData.Data
      await dispatch(updateShop(shop.ID, { ...shop, HasAcceptedHaynesproEULA: true }))
    }
  }, [isAcceptedEULA, isAcceptedHaynesPro, dispatch])

  return {
    Component: AcceptTermsAndConditionsStep,
    CTAComponent: AcceptTermsAndConditionsCTA,
    StepperText,
    isCompleted: complete.isCompleted,
    showInStepper: true,
    showStepperHeader: true,
    hideSkip: true,
    shouldExecuteStep,
    shouldRunInBackground,
    step: stepName,
    stepData,
    stepProps: {
      duplicationCheck: stepData.DuplicationCheck || [],
      basePath,
      onSubmit: complete.onSubmit,
      country,
      tempShopID: `${country}000000`,
      modules: onboardingInfo.modules
    },
    persister: {
      onPersist,
      persistOrder: 3
    }
  }
}

const StepperText: React.FC<{}> = () => {
  const { t } = useTranslation()
  return <>{t('onboarding.step_title_terms_and_conditions')}</>
}

export const AcceptTermsAndConditionsStep: React.FC<defaultStepFormProps & StepProps> = ({ basePath, tempShopID, ...rest }) => {
  return (
    <Route
      path={routing.pathAcceptTermsAndConditionsForm(basePath)}
      render={() => (
        <ShopCountryContextProvider shopID={tempShopID}>
          <OnboardingAcceptTermsAndConditions {...rest} />
        </ShopCountryContextProvider>
      )}
    />
  )
}
